import {useMemo} from "react";
import {useLocale} from "i18n";
import {bakedCurrencyForContext} from "pricing/bakedPricing";
import {useMountedOrigin} from "utils/useMountedOrigin";
import {referralIntroPricesFor, type ReferralIntroPrices} from "./referralOffer";

/**
 * What a referred friend pays up front on this storefront, for the surfaces that state the reward
 * WITHOUT a resolved storefront of their own (ui#1993, api#1228): the download page's invite panel, and
 * the account card the referrer repeats it from.
 *
 * A thin React adapter over {@link referralIntroPricesFor}, holding no rule of its own. Neither surface
 * has a quote to follow — the download page is anonymous, and the referrer's own card is about their
 * code rather than about a purchase — so the currency is resolved the way every other baked figure on
 * those pages resolves it: the reader's own STOREFRONT and the host serving the page, through
 * `bakedCurrencyFor`.
 *
 * <b>The pricing page's footnote is NOT one of these.</b> It sits beside cards that may already have
 * swapped to the api's storefront, so it reads {@link referralIntroPricesOf} off that quote instead —
 * while it asked this hook, a GB reader got pound cards over a euro promise.
 *
 * The host comes from {@link useMountedOrigin} rather than from a canonical constant, per the owner's
 * displayed-origin rule (ui#1517): what these pages quote must follow the origin the reader is actually
 * on, and `scripts/displayed-origin-sweep.mjs` is what keeps that true.
 *
 * `null` where this storefront prices neither tier, which both surfaces render as silence.
 */
export function useReferralIntroPrices(): ReferralIntroPrices | null {
	// The STOREFRONT locale, not the reading one (ui#2471): the two differ wherever a path prefix is only a
	// language, as on `transferify.ro/de/**`, which sells in lei. On the main host a subfolder IS the
	// storefront since kitecorp/transferify-api-service#2638, and `storefrontLocale` already carries that.
	const {storefrontLocale} = useLocale();
	const origin = useMountedOrigin();
	return useMemo(() => {
		const currency = bakedCurrencyForContext(storefrontLocale, hostnameOf(origin));
		return referralIntroPricesFor(currency);
	}, [storefrontLocale, origin]);
}

/**
 * The host out of an origin, tolerating the empty string a pre-paint render hands back.
 *
 * `new URL("")` throws, and this hook runs during the SSG prerender of every public route, so the throw
 * would be a blank page rather than a missing price.
 */
function hostnameOf(origin: string): string {
	try {
		return new URL(origin).hostname;
	} catch {
		return "";
	}
}
